import { apiService } from './apiService';
import { API_CONFIG } from '../config/apiConfig';
import { orderStatusService, PreparoStatusPayload, EntregaStatusPayload } from './orderStatusService';
import { Order, PaymentMethod, OrderItem, VendaFormaPagamento } from '../types';
import { getDateInBrasiliaTimezone } from '@/lib/utils';

interface ErpVendaRequest {
  ID: number;
  Data: string;
  FormaPagamentoID: number;
  VendaID: number;
  IdentificadorVendaPDV: string;
  /**
   * Status da mesa no ERP (apenas para operações de mesa):
   * 0=Indefinido, 1=Aberta, 2=Fechada, 3=Cancelada
   */
  StatusMesa?: number;
  EmpresaID: number;
  ClienteID: number;
  VendedorID: number;
  TipoOperacaoID: number;
  ValorDesconto: number;
  TotalPedido: number;
  Observacao?: string;
  CpfCnpjCliente?: string;
  Troco: number;
  NumeroComanda?: string;
  FaturaAutomatico?: boolean;
  VendaItem: ErpVendaItem[];
  VendaFormaPagamento: ErpVendaFormaPagamento[];
}

interface ErpVendaItem {
  ProdutoID: number;
  ProdutoNome: string;
  Quantidade: number;
  Valor: number;
  SubTotal: number;
  ValorDesconto: number;
  Total: number;
  UnidadeMedidaID: number;
  IdPedidoLocal?: string;
}

interface ErpVendaFormaPagamento {
  FormaPagamentoID: number;
  FormaPagamentoNome: string;
  Valor: number;
  DataVencimento: string;
}

const tryParseNumericId = (value: string | undefined | null): number | null => {
  if (!value) return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed > 0 ? Math.trunc(parsed) : null;
};

const resolvePaymentMethodErpId = (method: PaymentMethod): number | null => {
  if (typeof method.ID === 'number' && method.ID > 0) return method.ID;
  if (typeof method.FormaPagamentoID === 'number' && method.FormaPagamentoID > 0) return method.FormaPagamentoID;
  return tryParseNumericId(method.id);
};

export const generateGuid = (): string => {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
    const r = Math.random() * 16 | 0;
    const v = c === 'x' ? r : (r & 0x3 | 0x8);
    return v.toString(16);
  });
};

export function isValidGuid(str: string): boolean {
  const guidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
  return guidRegex.test(str);
}

type ProductIdSource = {
  productId?: string;
  product?: {
    id: string;
    name: string;
    erpProductId?: number;
    price: number;
  };
};

const resolveErpProdutoId = (item: any): number | null => {
  const erpId = item?.product?.erpProductId;
  if (typeof erpId === 'number' && Number.isFinite(erpId) && erpId > 0) return erpId;

  // Itens vindos do ERP (MesasAbertas / MesaPorId): normalmente vêm com `productId` numérico.
  const parsedProductId = Number(item?.productId);
  if (Number.isFinite(parsedProductId) && parsedProductId > 0) return parsedProductId;

  // Fallback: alguns fluxos usam `product.id` como numérico.
  const parsedProductInnerId = Number(item?.product?.id);
  if (Number.isFinite(parsedProductInnerId) && parsedProductInnerId > 0) return parsedProductInnerId;

  return null;
};

const mapOrderItemsToErpFormat = (items: OrderItem[]): ErpVendaItem[] => {
  return items.map(item => {
    const produtoId = resolveErpProdutoId(item as any);
    if (!produtoId) {
      throw new Error(
        `Produto sem ERP ID válido. Sincronize os produtos antes de enviar o pedido. ` +
        `Produto='${item.product.name}', idLocal='${item.product.id}', productId='${(item as any).productId}', erpProductId='${item.product.erpProductId}'`
      );
    }

    return {
      ProdutoID: produtoId,
      ProdutoNome: item.product.name,
      Quantidade: item.quantity,
      Valor: item.product.price,
      SubTotal: item.quantity * item.product.price,
      ValorDesconto: 0,
      Total: item.quantity * item.product.price,
      UnidadeMedidaID: 1,
      IdPedidoLocal: (item as any).IdPedidoLocal
    };
  });
};

export const getOrCreateTableExternalId = (tableId: string): string => {
  const tableExternalIdKey = `table_external_id_${tableId}`;
  let externalId = localStorage.getItem(tableExternalIdKey);
  
  if (!externalId || !isValidGuid(externalId)) {
    externalId = generateGuid();
    localStorage.setItem(tableExternalIdKey, externalId);
  }
  
  return externalId;
};

export const ensureOrderExternalId = (order: Order): Order => {
  if (!order.externalId || !isValidGuid(order.externalId)) {
    order.externalId = getOrCreateTableExternalId(order.tableId);
  }
  return order;
};

export const clearTableData = async (tableId: string): Promise<void> => {
  try {
    const tableExternalIdKey = `table_external_id_${tableId}`;
    localStorage.removeItem(tableExternalIdKey);
    
    // Arquivar micropedidos da sessão anterior
    const { getOrders, updateOrder, getTables, updateTable } = await import('./storageService');
    const allOrders = await getOrders();
    
    const tableOrders = allOrders.filter(order =>
      order.tableId === tableId && order.status === 'paid'
    );
    
    // Alterar status dos micropedidos pagos para 'archived'
    for (const order of tableOrders) {
      order.status = 'archived';
      await updateOrder(order);
    }
    
    // Limpar completamente a mesa (todos os pedidos)
    const tables = await getTables();
    const table = tables.find(t => t.id === tableId);
    if (table) {
      table.orders = []; // Limpar completamente o array de pedidos
      await updateTable(table);
    }
    
    console.log(`Table data cleared for table ID: ${tableId}. Archived ${tableOrders.length} micropedidos.`);
  } catch (error) {
    console.error('Error clearing table data:', error);
  }
};

const mapItemStatusToPreparo = (status: OrderItem['status']): string => {
  switch (status) {
    case 'preparing':
      return 'EmPreparo';
    case 'ready':
      return 'Pronto';
    case 'delivered':
      return 'EntregueAoGarcom';
    case 'cancelled':
      return 'Cancelado';
    default:
      return 'Aguardando';
  }
};

const mapDeliveryStatusToErp = (status?: Order['deliveryStatus']): string => {
  switch (status) {
    case 'preparing':
      return 'EmSeparacao';
    case 'ready_for_delivery':
    case 'ready_for_pickup':
      return 'Pronto';
    case 'out_for_delivery':
      return 'EmRota';
    case 'delivered':
    case 'picked_up':
      return 'Entregue';
    case 'cancelled':
      return 'Cancelado';
    default:
      return 'Pendente';
  }
};

const toNumberOrUndefined = (value?: string | number): number | undefined => {
  if (value === undefined || value === null) return undefined;
  const parsed = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(parsed) ? parsed : undefined;
};

const buildPreparoPayload = (order: Order): PreparoStatusPayload => ({
  status: 'Aguardando',
  estacao: order.type === 'delivery' ? 'cozinha' : 'balcao',
  itens: order.items.map(item => ({
    produtoId: item.product.erpProductId || toNumberOrUndefined(item.product.id),
    quantidade: item.quantity,
    status: mapItemStatusToPreparo(item.status),
    observacao: item.notes,
  })),
});

const buildEntregaPayload = (order: Order): EntregaStatusPayload => ({
  status: mapDeliveryStatusToErp(order.deliveryStatus),
  motorista: order.deliveryPersonName,
  janela: order.estimatedDeliveryTime ? `~${order.estimatedDeliveryTime} min` : undefined,
  tipoPedido: order.type,
  endereco: order.deliveryAddress,
  taxaEntrega: order.deliveryFee,
  observacao: order.deliveryNotes,
  eventos: [
    {
      data: new Date().toISOString(),
      tipo: 'PedidoSincronizado',
      comentario: order.deliveryNotes || 'Pedido sincronizado pelo PDV móvel',
    },
  ],
});

const shouldCreateDeliveryStatus = (order: Order): boolean =>
  order.type === 'delivery' || order.type === 'pickup' || !!order.deliveryAddress;

const initializeOrderStatuses = async (erpVendaId: number, order: Order): Promise<void> => {
  if (!erpVendaId) return;

  try {
    const preparoPayload = buildPreparoPayload(order);
    await orderStatusService.setPreparoStatus(erpVendaId, preparoPayload);
  } catch (error) {
    console.warn('Não foi possível registrar status de preparo no ERP.', error);
  }

  if (!shouldCreateDeliveryStatus(order)) {
    return;
  }

  try {
    const entregaPayload = buildEntregaPayload(order);
    await orderStatusService.setEntregaStatus(erpVendaId, entregaPayload);
  } catch (error) {
    console.warn('Não foi possível registrar status de entrega no ERP.', error);
  }
};

export const createErpVendaRequest = (
  order: Order, 
  paymentMethods?: PaymentMethod[],
  paymentValues?: number[],
  vendedorId: number = 1,
  clienteID: number = 0,
  faturarAutomatico: boolean = false,
  tableGuid?: string,
  statusMesa: number = 0
): ErpVendaRequest => {
  // Quando finalizamos uma mesa com múltiplos micropedidos, podemos precisar
  // forçar um GUID único para a sessão toda (agrupamento no ERP).
  if (tableGuid && isValidGuid(tableGuid)) {
    order.externalId = tableGuid;
  } else if (!order.externalId || !isValidGuid(order.externalId)) {
    order.externalId = getOrCreateTableExternalId(order.tableId);
  }

  let vendaFormaPagamento: ErpVendaFormaPagamento[] = [];

  const brasiliaNowIso = getDateInBrasiliaTimezone(order.createdAt).toISOString();

  const hasExplicitPayments =
    paymentMethods &&
    paymentMethods.length > 0 &&
    paymentValues &&
    paymentValues.length === paymentMethods.length;

  if (hasExplicitPayments) {
    vendaFormaPagamento = paymentMethods!.map((method, index) => {
      const resolvedId = resolvePaymentMethodErpId(method);
      return {
        FormaPagamentoID: resolvedId || 1,
        FormaPagamentoNome: method.name,
        Valor: paymentValues![index],
        DataVencimento: brasiliaNowIso
      };
    });
  } else if (order.VendaFormaPagamento && order.VendaFormaPagamento.length > 0) {
    vendaFormaPagamento = order.VendaFormaPagamento.map(fp => ({
      FormaPagamentoID: fp.FormaPagamentoID,
      FormaPagamentoNome: fp.FormaPagamentoNome,
      Valor: fp.Valor,
      DataVencimento: fp.DataVencimento || brasiliaNowIso
    }));
  } else if (paymentMethods && paymentMethods.length > 0) {
    let effectiveMethods = paymentMethods;

    if (order.paymentMethodId) {
      const selected = paymentMethods.find(m => m.id === order.paymentMethodId || String(m.ID || '') === order.paymentMethodId);
      if (selected) {
        effectiveMethods = [selected];
      }
    }

    if (effectiveMethods.length === 1) {
      const resolvedId = resolvePaymentMethodErpId(effectiveMethods[0]);
      vendaFormaPagamento = [{
        FormaPagamentoID: resolvedId || 1,
        FormaPagamentoNome: effectiveMethods[0].name,
        Valor: order.totalAmount,
        DataVencimento: brasiliaNowIso
      }];
    } else {
      // Não há como inferir rateio entre múltiplas formas sem valores explícitos.
      // Mantém comportamento seguro (fallback Dinheiro) para não enviar payload inconsistente.
      vendaFormaPagamento = [{
        FormaPagamentoID: 1,
        FormaPagamentoNome: 'Dinheiro',
        Valor: order.totalAmount,
        DataVencimento: brasiliaNowIso
      }];
      console.warn('Payment values ausentes/mismatch; usando fallback Dinheiro.', {
        orderId: order.id,
        paymentMethodsCount: paymentMethods.length,
        paymentValuesCount: paymentValues?.length
      });
    }
  } else {
    vendaFormaPagamento = [{
      FormaPagamentoID: 1,
      FormaPagamentoNome: 'Dinheiro',
      Valor: order.totalAmount,
      DataVencimento: brasiliaNowIso
    }];
  }
  
  const brasiliaDate = getDateInBrasiliaTimezone(order.createdAt);
  
  const finalClienteID = clienteID || (order.customerId ? parseInt(order.customerId, 10) : 0);
  
  console.log(`ClienteID para sincronização: ${finalClienteID}`);
  console.log(`ExternalID (GUID) para sincronização: ${order.externalId}`);
  console.log(`Documento para sincronização: ${order.document || 'Não informado'}`);
  console.log(`Faturar automático: ${faturarAutomatico}`);
  
  const principalPaymentMethodId = vendaFormaPagamento.length > 0
    ? vendaFormaPagamento[0].FormaPagamentoID
    : 1;

  const normalizedStatusMesa = [0, 1, 2, 3].includes(statusMesa) ? statusMesa : 0;
  
  const vendaRequest: ErpVendaRequest = {
    ID: order.erpId || 0,
    Data: brasiliaDate.toISOString(),
    FormaPagamentoID: principalPaymentMethodId,
    VendaID: order.erpId || 0,
    IdentificadorVendaPDV: order.externalId,
    StatusMesa: normalizedStatusMesa,
    EmpresaID: parseInt(localStorage.getItem('empresa_id') || '1', 10),
    ClienteID: finalClienteID,
    VendedorID: vendedorId,
    TipoOperacaoID: 0,
    ValorDesconto: 0,
    TotalPedido: order.totalAmount,
    Observacao: order.observations || "",
    CpfCnpjCliente: order.document || "",
    Troco: order.changeAmount || 0,
    NumeroComanda: order.tableNumber?.toString() || "",
    FaturaAutomatico: faturarAutomatico,
    VendaItem: mapOrderItemsToErpFormat(order.items),
    VendaFormaPagamento: vendaFormaPagamento
  };
  
  return vendaRequest;
};

export const syncOrderWithErp = async (
  order: Order, 
  paymentMethods?: PaymentMethod[],
  paymentValues?: number[],
  vendedorId: number = 1,
  clienteID: number = 0,
  faturarAutomatico: boolean = false,
  tableGuid?: string,
  statusMesa: number = 0
): Promise<number> => {
  try {
    API_CONFIG.initialize();

    // Se o pedido ainda não tem `erpId`, trata como “novo/pdv” => pode inicializar status no ERP.
    // Se já tem `erpId`, veio do ERP => NÃO sobrescrever status de preparo/entrega.
    const shouldInitializeStatuses = !order.erpId;

    if (!API_CONFIG.baseURL) {
      console.error('API base URL is not configured');
      throw new Error('URL da API não configurada');
    }
    
    const endpoint = API_CONFIG.endpoints.orders.syncOrder;
    const vendaRequest = createErpVendaRequest(order, paymentMethods, paymentValues, vendedorId, clienteID, faturarAutomatico, tableGuid, statusMesa);
    
    console.log('Sync order with ERP:', {
      baseURL: API_CONFIG.baseURL,
      endpoint,
      hasApiKey: !!API_CONFIG.apiKey,
      order: {
        id: order.id,
        status: order.status,
        totalAmount: order.totalAmount,
        itemsCount: order.items.length,
        externalId: order.externalId,
        document: order.document,
        observations: order.observations
      },
      paymentMethods: paymentMethods?.map(pm => pm.name) || 'Não especificado',
      vendedorId,
      clienteID,
      faturarAutomatico,
      tableGuid,
      statusMesa
    });
    
    console.log('Sync request data:', vendaRequest);
    
    const response = await apiService.post(endpoint, vendaRequest);
    
    console.log('Sync response:', response);

    if (vendaRequest.VendaFormaPagamento && vendaRequest.VendaFormaPagamento.length > 0) {
      order.VendaFormaPagamento = vendaRequest.VendaFormaPagamento;
      const { updateOrder } = await import('./storageService');
      await updateOrder(order);
      console.log('Updated order with payment methods:', order.VendaFormaPagamento);
    }

    let vendaId = 0;
    if (typeof response === 'number') {
      vendaId = response;
    } else if (response && typeof response.ID === 'number') {
      vendaId = response.ID;
    } else {
      console.warn('Unexpected response format:', response);
    }

    if (vendaId > 0 && shouldInitializeStatuses) {
      await initializeOrderStatuses(vendaId, order);
    }

    return vendaId;
  } catch (error) {
    console.error('Error syncing order with ERP:', error);
    throw error;
  }
};

export interface DanfeResponse {
  mensagem: string;
  success: boolean;
  danfe: string | null;
  xml: string | null;
}

export interface PedidoCompletoResponse {
  success: boolean;
  pedido?: {
    venda: {
      id: number;
      data: string;
      dataCriacao?: string;
      status?: string;
      origem?: string;
      totalItens: number;
      totalDesconto: number;
      totalAcrescimo: number;
      totalFinal: number;
      dadosAdicionais?: string;
      numeroNota?: string | null;
      chaveNota?: string | null;
      tipoOperacao?: { id: number; descricao?: string; modeloDocumento?: string };
      vendedor?: { id: number; nome?: string };
    };
    cliente?: {
      id: number;
      pessoaID?: number;
      nome?: string;
      documento?: string;
      contatos?: Array<{ tipo?: string; contato?: string }>;
      endereco?: {
        logradouro?: string;
        numero?: string;
        complemento?: string;
        bairro?: string;
        cep?: string;
        municipio?: string;
        uf?: string;
      };
    };
    itens: Array<{
      produtoID: number;
      produtoNome: string;
      ean?: string;
      quantidade: number;
      precoUnitario: number;
      desconto: number;
      totalFinal: number;
      unidadeMedida?: string;
    }>;
    pagamentos?: Array<{
      id: number;
      formaPagamentoID: number;
      descricao?: string;
      valor: number;
      dataEmissao?: string;
      dataVencimento?: string | null;
    }>;
    empresa?: {
      id: number;
      nomeFantasia?: string;
      razaoSocial?: string;
      cnpj?: string;
      telefone?: string;
      endereco?: {
        logradouro?: string;
        numero?: string;
        complemento?: string;
        bairro?: string;
        cep?: string;
        municipio?: string;
        uf?: string;
      };
      logoBase64?: string;
    };
  };
  message?: string;
}

export type PedidoCompleto = NonNullable<PedidoCompletoResponse['pedido']>;

export const requestDanfePdf = async (orderId: number): Promise<DanfeResponse> => {
  try {
    API_CONFIG.initialize();
    
    if (!API_CONFIG.baseURL) {
      console.error('API base URL is not configured');
      throw new Error('URL da API não configurada');
    }
    
    const endpoint = '/api/vendas/ImprimirDanfePorID';
    
    console.log(`Requesting DANFE for order ID: ${orderId}`);
    
    const response = await apiService.post<DanfeResponse>(endpoint, orderId);
    
    console.log('DANFE response received:', {
      success: response.success,
      hasMessage: !!response.mensagem,
      hasPdf: !!response.danfe,
      hasXml: !!response.xml
    });
    
    return {
      mensagem: response.mensagem || '',
      success: response.success || false,
      danfe: response.danfe || null,
      xml: response.xml || null
    };
  } catch (error) {
    console.error('Error requesting DANFE PDF:', error);
    throw error;
  }
};

export const getPedidoCompleto = async (vendaID: number): Promise<PedidoCompleto> => {
  API_CONFIG.initialize();

  if (!API_CONFIG.baseURL) {
    console.error('API base URL is not configured');
    throw new Error('URL da API não configurada');
  }

  const endpoint = API_CONFIG.endpoints.orders.getComplete;
  const response = await apiService.get<PedidoCompletoResponse>(endpoint, { vendaID });

  if (!response?.success || !response.pedido) {
    throw new Error(response?.message || 'Venda não encontrada');
  }

  return response.pedido;
};

export const testApiConnection = async (): Promise<boolean> => {
  try {
    API_CONFIG.initialize();
    
    if (!API_CONFIG.baseURL) {
      console.error('API base URL is not configured');
      return false;
    }
    
    const endpoint = '/api/ping';
    const response = await apiService.get(endpoint);
    
    return !!response;
  } catch (error) {
    console.error('Error testing API connection:', error);
    return false;
  }
};

export const isOrderModifiedSinceLastSync = (order: Order): boolean => {
  if (!order.lastSyncDate) return true;
  
  const lastSyncTime = new Date(order.lastSyncDate).getTime();
  const updateTime = new Date(order.updatedAt).getTime();
  
  return updateTime > lastSyncTime;
};

export const calculateSubtotal = (order: Order): number => {
  if (!order.items || order.items.length === 0) return 0;
  
  return order.items.reduce((sum, item) => {
    if (item.status === 'cancelled') return sum;
    return sum + (item.product.price * item.quantity);
  }, 0);
};

export const calculateDiscountValue = (order: Order): number => {
  const subtotal = calculateSubtotal(order);
  
  if (!order.discount) return 0;
  
  if (order.discountType === 'percentage') {
    return (subtotal * order.discount) / 100;
  } else {
    return Math.min(order.discount, subtotal);
  }
};

export const calculateOrderTotal = (order: Order): number => {
  const subtotal = calculateSubtotal(order);
  const discountValue = calculateDiscountValue(order);
  
  return subtotal - discountValue;
};

export const calculateTableTotal = (orders: Order[]): number => {
  if (!orders || orders.length === 0) return 0;
  
  return orders.reduce((sum, order) => {
    if (order.status === 'cancelled') return sum;
    return sum + calculateOrderTotal(order);
  }, 0);
};
